import usePublicationList from '@/lib/hooks/usePublicationList';
import { usePublicationStore } from '@/lib/store/publication-list';
import { useSession } from 'next-auth/react';
import { useEffect } from 'react';
import { Hits, useInstantSearch } from 'react-instantsearch';
import { toast } from 'sonner';
import FiltersWithSearch from './filters-with-search';
import PublicationListController from './publication-list/controller';
import ClearRefinements from './widgets/clearRefinements';
import CustomPagination from './widgets/customPagination';
import Error from './widgets/error';
import LoadingIndicator from './widgets/loadingIndicator';
import NoResults from './widgets/noResults';
import NoResultsBoundary from './widgets/noResultsBoundary';
import PublicationCardHit from './widgets/publicationCardHit';
import ShowingTotalResults from './widgets/total-results';

const PublicationList = ({
  handlePublicationInquiryOpen,
  handleRequirementsOpen,
  isWhitelabelOwner,
  listName = '',
  publicationListId,
  isShared,
}) => {
  const { status } = useSession();
  const { setIndexUiState } = useInstantSearch();

  const { selectedSavedList, selectedPublications } = usePublicationStore(
    (state) => state
  );

  const { data: publicationList } = usePublicationList(publicationListId);

  useEffect(() => {
    if (!publicationList) return;

    setIndexUiState((prevIndexUiState) => {
      return {
        ...prevIndexUiState,
        refinementList: {
          ...prevIndexUiState.refinementList,
          id: publicationList?.publications?.map((p) => p.id.toString()),
          location: [],
        },
        page: 0,
        query: '',
        sortBy: '',
        range: {
          price: '0',
          domain_authority: '0',
          domain_ranking: '0',
          min_turnaround_time_in_days: '0',
          max_turnaround_time_in_days: '0',
        },
        menu: {
          'publication_categories.name': '',
        },
        toggle: {
          is_do_follow: false,
          is_sponsored: false,
          news: false,
        },
      };
    });
  }, [publicationList, setIndexUiState]);

  const hasListName = listName || selectedSavedList?.name;

  const handleCopyClipboard = () => {
    const queries = window.location.search;
    const route = `${window.location.origin}/shared-publications/${publicationListId}`;

    const search = `${route}${queries}`;

    navigator.clipboard
      .writeText(search)
      .then(() => toast.success('Link Copied!'));
  };

  const hasUser = status === 'authenticated';

  return (
    <>
      {hasUser && (
        <div className="flex justify-between items-center">
          {hasListName && <ClearRefinements action="back" />}
          {(selectedPublications?.length > 0 || hasListName) && (
            <FiltersWithSearch className="ml-auto" />
          )}
        </div>
      )}
      <div id="press-lists">
        <div className="flex flex-col md:flex-row gap-6 md:items-center justify-between my-6">
          <h2 className="text-left text-3xl sm:text-[2.5rem] sm:leading-none text-primary-900 flex-1">
            {hasListName ? hasListName : 'All Publications'}
          </h2>
          {!hasUser && (
            <FiltersWithSearch className="flex justify-between items-center flex-wrap gap-4" />
          )}
          {hasUser && (
            <PublicationListController
              handleCopyClipboard={handleCopyClipboard}
              hasListName={hasListName}
            />
          )}
        </div>
        <ShowingTotalResults />
        <Error />
        <NoResultsBoundary fallback={<NoResults />}>
          <LoadingIndicator />
          <Hits
            hitComponent={({ hit }) => (
              <PublicationCardHit
                data={hit}
                handlePublicationInquiryOpen={handlePublicationInquiryOpen}
                handleRequirementsOpen={handleRequirementsOpen}
                isWhitelabelOwner={isWhitelabelOwner}
                hasListName={hasListName}
                hasUser={hasUser}
              />
            )}
            classNames={{ list: 'space-y-2' }}
          />
        </NoResultsBoundary>
      </div>
      <CustomPagination />
    </>
  );
};

export default PublicationList;
